/**
 * ReassignTransferModal Component
 * Modal for reassigning a transfer to a different account/transaction.
 * WP-TRANSFER-4: Reassign Transfer Pair
 *
 * Follows the same pattern as MarkTransferModal.tsx:
 * - Step 1: Account selection dropdown (pre-select current target if exists)
 * - Step 2: Optional transaction candidate selection
 * - Submit: Call reassignTransfer() API
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Spinner, Alert, Select } from '@/components/ui';
import { formatCurrency, formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';
import {
  transferService,
  type TransferCandidate,
  type ReassignTransferResponse,
} from '@/services/transactions';
import type { TransactionWithSplits } from '@/services/transactions';
import type { Account } from '@/types';
import { ApiClientError } from '@/services/api';

// ============================================================================
// Types
// ============================================================================

export interface ReassignTransferModalProps {
  transaction: TransactionWithSplits;
  accounts: Account[];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: ReassignTransferResponse) => void;
}

// ============================================================================
// Component
// ============================================================================

export default function ReassignTransferModal({
  transaction,
  accounts,
  isOpen,
  onClose,
  onSuccess,
}: ReassignTransferModalProps) {
  // State
  const [targetAccountId, setTargetAccountId] = useState<string>('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<TransferCandidate[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out the current transaction's account from the list
  const availableAccounts = useMemo(() => {
    return accounts.filter((acc) => acc.id !== transaction.accountId && !acc.isHidden);
  }, [accounts, transaction.accountId]);

  // Convert accounts to select options
  const accountOptions = useMemo(() => {
    return availableAccounts.map((acc) => ({
      value: acc.id,
      label: acc.mask ? `${acc.name} (***${acc.mask})` : acc.name,
    }));
  }, [availableAccounts]);

  // Reset state when modal opens/closes
  // Pre-select current target account if it exists
  useEffect(() => {
    if (isOpen) {
      // Pre-select current target account if it exists and is available
      const currentTargetId = transaction.transferTargetAccountId ?? '';
      const isTargetAvailable = availableAccounts.some((acc) => acc.id === currentTargetId);
      setTargetAccountId(isTargetAvailable ? currentTargetId : '');
      setSelectedCandidateId(null);
      setCandidates([]);
      setError(null);
    }
  }, [isOpen, transaction.transferTargetAccountId, availableAccounts]);

  // Fetch candidates when target account changes
  useEffect(() => {
    if (!targetAccountId || !isOpen) {
      setCandidates([]);
      setSelectedCandidateId(null);
      return;
    }

    const fetchCandidates = async () => {
      setIsLoadingCandidates(true);
      setError(null);
      try {
        const result = await transferService.getTransferCandidates(
          transaction.id,
          targetAccountId
        );
        setCandidates(result);
        // Auto-select the best match if it has a high score
        const bestMatch = result[0];
        if (result.length > 0 && bestMatch && bestMatch.matchScore >= 0.8) {
          setSelectedCandidateId(bestMatch.id);
        } else {
          setSelectedCandidateId(null);
        }
      } catch (err) {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to load transfer candidates');
        }
      } finally {
        setIsLoadingCandidates(false);
      }
    };

    fetchCandidates();
  }, [targetAccountId, transaction.id, isOpen]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!targetAccountId) {
      setError('Please select a target account');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await transferService.reassignTransfer(transaction.id, {
        targetAccountId,
        pairedTransactionId: selectedCandidateId ?? undefined,
      });
      onSuccess(response);
      onClose();
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to reassign transfer');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [targetAccountId, selectedCandidateId, transaction.id, onSuccess, onClose]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const displayName = transaction.merchantName || transaction.name || 'Unknown';
  const isNegative = transaction.amount < 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reassign-transfer-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            id="reassign-transfer-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Reassign Transfer Pair
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Transaction info */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-500 mb-1">Transaction</p>
          <p className="font-medium text-gray-900">{displayName}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm text-gray-500">
              {formatDate(transaction.date)}
            </span>
            <span
              className={cn(
                'font-semibold',
                isNegative ? 'text-gray-900' : 'text-green-600'
              )}
            >
              {isNegative ? '-' : '+'}
              {formatCurrency(Math.abs(transaction.amount), transaction.currency)}
            </span>
          </div>
        </div>

        {/* Current pairing info */}
        {transaction.transferTargetAccountName && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Current pair:</span>{' '}
              {transaction.transferTargetAccountName}
              {transaction.transferPairTransaction && (
                <span className="text-blue-600">
                  {' - '}
                  {formatCurrency(Math.abs(transaction.transferPairTransaction.amount), transaction.currency)}
                </span>
              )}
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Target account selection */}
        <div className="mb-6">
          <Select
            label="Transfer to Account"
            placeholder="Select target account..."
            options={accountOptions}
            value={targetAccountId}
            onChange={(e) => setTargetAccountId(e.target.value)}
            disabled={isSubmitting}
          />
          <p className="mt-1 text-xs text-gray-500">
            Select the account where this money was transferred to/from.
          </p>
        </div>

        {/* Transfer candidates */}
        {targetAccountId && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link to Matching Transaction (Optional)
            </label>

            {isLoadingCandidates ? (
              <div className="flex items-center justify-center py-4">
                <Spinner size="sm" />
                <span className="ml-2 text-sm text-gray-500">Finding matches...</span>
              </div>
            ) : candidates.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">
                  No matching transactions found in the selected account.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  The transfer will be linked to the account without a paired transaction.
                </p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {/* No link option */}
                <label
                  className={cn(
                    'flex items-center px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50',
                    selectedCandidateId === null && 'bg-primary-50'
                  )}
                >
                  <input
                    type="radio"
                    name="candidate"
                    checked={selectedCandidateId === null}
                    onChange={() => setSelectedCandidateId(null)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    Do not link to any transaction
                  </span>
                </label>

                {/* Candidate list */}
                {candidates.map((candidate) => (
                  <CandidateRow
                    key={candidate.id}
                    candidate={candidate}
                    isSelected={selectedCandidateId === candidate.id}
                    onSelect={() => setSelectedCandidateId(candidate.id)}
                    currency={transaction.currency}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!targetAccountId || isSubmitting || isLoadingCandidates}
            isLoading={isSubmitting}
          >
            Reassign Transfer
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface CandidateRowProps {
  candidate: TransferCandidate;
  isSelected: boolean;
  onSelect: () => void;
  currency: string;
}

function CandidateRow({ candidate, isSelected, onSelect, currency }: CandidateRowProps) {
  const isNegative = candidate.amount < 0;

  // Format match score as percentage
  const matchPercent = Math.round(candidate.matchScore * 100);
  const matchColor =
    matchPercent >= 80
      ? 'text-green-600 bg-green-50'
      : matchPercent >= 50
      ? 'text-yellow-600 bg-yellow-50'
      : 'text-gray-600 bg-gray-50';

  return (
    <label
      className={cn(
        'flex items-center px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50',
        isSelected && 'bg-primary-50'
      )}
    >
      <input
        type="radio"
        name="candidate"
        checked={isSelected}
        onChange={onSelect}
        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
      />
      <div className="ml-3 flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {candidate.name}
        </p>
        <p className="text-xs text-gray-500">
          {formatDate(candidate.date)}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-sm font-medium whitespace-nowrap',
            isNegative ? 'text-gray-900' : 'text-green-600'
          )}
        >
          {isNegative ? '-' : '+'}
          {formatCurrency(Math.abs(candidate.amount), currency)}
        </span>
        <span className={cn('text-xs px-1.5 py-0.5 rounded', matchColor)}>
          {matchPercent}%
        </span>
      </div>
    </label>
  );
}
